import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { Platform } from "0.Mine/0.Components/CMS/ToTranslate/PlatformSelector";
import { UseMetricool } from "0.Mine/1.Hooks/social/metricool";
import { Post } from "0.Mine/3.Interfaces/PostResponse";

interface PostOptions {
  businessId: number;
  postContent: string;
  selectedPlatforms: string[];
  platforms: Platform[];
  mediaAttachments: File[];
  imageUrls?: string[];
  scheduleDate?: string;
  scheduleTime?: string;
  addPostToCalendar: (post: Post) => void;
}

export const useSocialPost = () => {
  const { postOnSocial } = UseMetricool();
  const [isPosting, setIsPosting] = useState(false);

  const validatePlatforms = (
    postContent: string,
    mediaAttachments: File[],
    selectedPlatformIds: string[],
    platformsList: Platform[],
    imageUrls: string[] = [] // Add imageUrls parameter
  ): { valid: boolean; errorMessages: string[] } => {
    // Instagram requires at least one image
    if (
      selectedPlatformIds.some((id) => {
        const platform = platformsList.find((p) => p.id === id);
        return platform?.type.toLowerCase() === "instagram";
      }) &&
      mediaAttachments.length === 0 &&
      imageUrls.length === 0
    ) {
      // Check both mediaAttachments and imageUrls
      return {
        valid: false,
        errorMessages: ["Instagram posts require at least one image"],
      };
    }

    // Facebook has a character limit
    if (
      selectedPlatformIds.some((id) => {
        const platform = platformsList.find((p) => p.id === id);
        return platform?.type.toLowerCase() === "facebook";
      }) &&
      postContent.length > 5000
    ) {
      return {
        valid: false,
        errorMessages: ["Facebook posts must be under 5000 characters"],
      };
    }

    // Twitter has a character limit
    if (
      selectedPlatformIds.some((id) => {
        const platform = platformsList.find((p) => p.id === id);
        return platform?.type.toLowerCase() === "twitter";
      }) &&
      postContent.length > 280
    ) {
      return {
        valid: false,
        errorMessages: ["Twitter posts must be under 280 characters"],
      };
    }

    // LinkedIn has a character limit
    if (
      selectedPlatformIds.some((id) => {
        const platform = platformsList.find((p) => p.id === id);
        return platform?.type.toLowerCase() === "linkedin";
      }) &&
      postContent.length > 3000
    ) {
      return {
        valid: false,
        errorMessages: ["LinkedIn posts must be under 3000 characters"],
      };
    }

    return { valid: true, errorMessages: [] };
  };

  // The unified post function
  const handlePost = async ({
    businessId,
    postContent,
    selectedPlatforms,
    platforms,
    mediaAttachments,
    imageUrls = [],
    scheduleDate,
    scheduleTime,
    addPostToCalendar,
  }: PostOptions): Promise<{ success: boolean; message: string }> => {
    setIsPosting(true);

    try {
      // Validate all selected platforms
      const selectedPlatformObjects = platforms.filter((p) => selectedPlatforms.includes(p.id));

      if (selectedPlatformObjects.length === 0) {
        return { success: false, message: "Seleziona almeno una piattaforma" };
      }

      // Check if scheduling is requested and validate schedule parameters
      if (scheduleDate && !scheduleDate) {
        return {
          success: false,
          message: "Data e ora di pubblicazione sono richiesti per schedulare un post",
        };
      }

      // Validate platforms based on content
      const validation = validatePlatforms(
        postContent,
        mediaAttachments,
        selectedPlatforms,
        platforms,
        imageUrls
      );

      if (!validation.valid) {
        const errorMessages = validation.errorMessages.join("\n");
        enqueueSnackbar(`Impossibile pubblicare su alcune piattaforme:\n${errorMessages}`, {
          variant: "error",
        });
        return { success: false, message: errorMessages };
      }

      // Create post date
      let postDate;
      let isScheduled = false;

      if (scheduleDate) {
        postDate = new Date(scheduleDate);
        isScheduled = true;
      } else {
        postDate = new Date();
      }

      // Post to social media platforms
      const response = await postOnSocial(
        businessId,
        postContent,
        selectedPlatformObjects.map((p) => p.type.toLowerCase()),
        mediaAttachments,
        postDate,
        imageUrls
      );

      if (!response || !response.success) {
        throw new Error(response?.message || "Failed to post to social media");
      }

      // Create a formatted date string for display
      const formattedDateTime = isScheduled
        ? `${scheduleDate}T${scheduleTime}`
        : new Date().toISOString();

      // Create post objects for each platform
      const newPosts = selectedPlatformObjects.map((platform) => {
        const post = {
          id: `post-${Date.now()}-${platform.id}-${Math.random().toString(36).substring(7)}`,
          text: postContent,
          network: platform.type.toLowerCase(),
          media: platform.type,
          scheduledDate: postDate,
          metrics: {
            IMPRESSIONS: 0,
            ENGAGEMENTS: 0,
            CLICKS: 0,
          },
          link: null,
          picture: mediaAttachments.length > 0 ? URL.createObjectURL(mediaAttachments[0]) : null,
          publicationDate: {
            dateTime: formattedDateTime,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        } as Post;

        return post;
      });

      // Add each post to the calendar
      try {
        await Promise.all(
          newPosts.map(async (post) => {
            await addPostToCalendar(post);
          })
        );
      } catch (error) {
        throw new Error("Failed to add one or more posts to calendar");
      }

      // Success message varies based on whether post was scheduled or immediate
      const successMessage = isScheduled
        ? `Post schedulato per ${postDate.toLocaleString("it-IT", {
            weekday: "long",
          })}`
        : "Il tuo post è partito! Tra pochissimo sarà online sui tuoi social. Rilassati, ci pensa QlickUp!";

      enqueueSnackbar(successMessage, { variant: "success" });
      return { success: true, message: successMessage };
    } catch (error) {
      const errorMessage = "Si è verificato un errore durante la pubblicazione del post";
      enqueueSnackbar(errorMessage, { variant: "error" });
      return { success: false, message: errorMessage };
    } finally {
      setIsPosting(false);
    }
  };

  return {
    handlePost,
    isPosting,
    validatePlatforms,
  };
};
